import React, { useEffect, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { SortingRule } from 'react-table';
import { Col, Row } from 'reactstrap';
import ButtonComponent from '../../components/ButtonComponent';
import InputComponent from '../../components/InputComponent';
import Layout from '../../components/Layout/Layout';
import TableComponent, { OperationsCell } from '../../components/TableComponent';
import RoutesObject from '../../helpers/Routes';
import Toast from '../../helpers/Toast';
import { Utilities } from '../../helpers/Utilities';
import { Parameter } from '../../models/Parameter';
import { PartDTO } from '../../models/Parts/PartDTO';
import { PartService } from '../../services/PartService';
import { useAvailableModulesContext } from '../../context/AvailableModulesContext';
import Routes from '../../helpers/Routes';

function PartList() {

    const intl = useIntl();
    const navigate = useNavigate();
    const [code, setCode] = useState<string>(
        Utilities.LoadParameterFromURLQuery("code", "string", ""),
    );
    const [name, setName] = useState<string>(
        Utilities.LoadParameterFromURLQuery("name", "string", ""),
    );
    const [data, setData] = useState<PartDTO[]>([]);
    const partService = new PartService();
    const [initialSortBy] = useState<SortingRule<object>[]>(
        Utilities.LoadSortParametersFromURLQuery("sortField", "sortOrder"),
    );
    const pageSizes: number[] = [5, 15, 30];
    const [goToPage, setGoToPage] = useState<number>();
    const [loading, setLoading] = useState(false);
    const [pageCount, setPageCount] = useState(1);
    const [page] = useState<number>(Utilities.LoadPageFromURLQuery("page"));
    const [pageSize] = useState<number>(Utilities.LoadPageSizeFromURLQuery(pageSizes));
    const [currentPage, setCurrentPage] = useState<number>();
    const [currentPageSize, setCurrentPageSize] = useState<number>();
    const [currentSorting, setCurrentSorting] = useState<SortingRule<object>[] | null>(null);
    const [reloadSearch, setReloadSearch] = useState<boolean>(false);
    const [searchParams] = useSearchParams();

    const [isActiveErpModule, setIsActiveErpModule] = useState<boolean>(false);
    const { availableModules } = useAvailableModulesContext();

    useEffect(() => {
        if (availableModules.includes("IsActiveErpModule")) {
            setIsActiveErpModule(true);
        }
    }, [availableModules]);

    const onEnter = () => {
        setCurrentPage(1);
        setGoToPage(1);
        fetchData(currentPage ?? 1, currentPageSize ?? 5, currentSorting);
    };

    const onButtonClick = async () => {
        setCurrentPage(1);
        setGoToPage(1);
        fetchData(currentPage ?? 1, currentPageSize ?? 5, currentSorting);
    };

    const onRemoveFiltersClick = async () => {
        setCurrentPage(1);
        setGoToPage(1);
        setCode("");
        setName("");
        setReloadSearch(true);
    };

    const columns = React.useMemo(() => {
        const commonColumns = [
            {
                Header: <FormattedMessage id="Screens.Parts.Table.Column.Code" />,
                accessor: "erpId",
                width: isActiveErpModule ? 30 : 25,
                Cell: (props: any) => {
                    return props.cell.row.original.erpId;
                },
            },
            {
                Header: <FormattedMessage id="Screens.Parts.Table.Column.Name" />,
                accessor: "name",
                width: 70,
                Cell: (props: any) => {
                    return props.cell.row.original.name;
                },
            },
        ];

        // Adiciona a nova coluna no início do array quando isActiveErpModule é false
        if (!isActiveErpModule) {
            commonColumns.unshift({
                Header: <FormattedMessage id="Screens.Parts.Table.Column.Index" />,
                accessor: " ",
                width: 5,
                Cell: (props: any) => {
                    return OperationsCell(props.cell.row.original.erpId, "part");
                }
            });
        }

        return commonColumns;
    }, [isActiveErpModule, availableModules]);

    const mountSearchParameters = (): Parameter[] => {
        var parameters: Parameter[] = [];

        parameters.push(new Parameter("code", code));
        parameters.push(new Parameter("name", name));

        return parameters;
    };

    const fetchData = async (page: number, pageSize: number, sortBy: SortingRule<object>[] | null,) => {
        var searchUrl: string =
            `?page=${page}&pageSize=${pageSize}${Utilities.AddParamToQueryIfValue(
                "code",
                "string",
                code,
            )}${Utilities.AddParamToQueryIfValue(
                "name",
                "string",
                name,
            )}`;

        if (sortBy != null && sortBy.length > 0) {
            searchUrl += `${Utilities.AddParamToQueryIfValue(
                "sortField",
                "string",
                sortBy[0].id,
            )}${Utilities.AddParamToQueryIfValue(
                "sortOrder",
                "string",
                sortBy[0].desc === true ? "DESC" : "ASC",
            )}`;
        }

        navigate({
            pathname: RoutesObject.parts.list,
            search: searchUrl,
        });


        setLoading(true);
        var response = await partService.GetAll(
            mountSearchParameters(),
            Utilities.MountSortParameters(sortBy),
            page,
            pageSize,
        );

        if (response.success !== true) {
            setData([]);
            setPageCount(0);
            setLoading(false);
            setCurrentPageSize(0);
            setCurrentPage(0);
            setCurrentSorting(null);
            Toast.Show("error", response.message);
            return;
        }

        setGoToPage(0)
        setData(response.items);
        setPageCount(response.totalPages);
        setLoading(false);
        setCurrentPageSize(pageSize);
        setCurrentPage(page);
        setCurrentSorting(sortBy);
        if (response.totalPages > 0 && page > response.totalPages) {
            setGoToPage(1);
        }
    };

    useEffect(() => {
        if (reloadSearch === true) {
            fetchData(currentPage ?? 1, currentPageSize ?? 5, currentSorting);
            setReloadSearch(false);
        }
        return () => { };
    }, [reloadSearch]);

    const onNewTaskClick = () => {
        navigate(Routes.parts.create);
    };

    return (
        <Layout>
            <div className="pageContainer">
                <div className="pageHeader">
                    <span className="pageTitle">
                        <FormattedMessage id="Screens.Parts.Title" />
                    </span>
                </div>

                {!isActiveErpModule && (
                    <Row>
                        <Col xl={2} lg={3} md={3} sm={12} xs={12} style={{ marginTop: "1em" }}>
                            <ButtonComponent
                                text={intl.formatMessage({ id: "Screens.Parts.Button.CreatePart" })}
                                onClick={onNewTaskClick} />
                        </Col>
                    </Row>
                )}

                <div className="pageBody">
                    <Row>
                        <InputComponent xl={2} lg={3} md={5} sm={12} xs={12}
                            style={{ width: "100%" }}
                            type={"text"}
                            onEnter={onEnter}
                            value={code}
                            label={intl.formatMessage({ id: "Screens.Parts.Search.Code" })}
                            readOnly={false}
                            onChange={(t: string) => setCode(t)}
                        />
                        <InputComponent xl={2} lg={3} md={5} sm={12} xs={12}
                            style={{ width: "100%" }}
                            type={"text"}
                            onEnter={onEnter}
                            value={name}
                            label={intl.formatMessage({ id: "Screens.Parts.Search.Name" })}
                            readOnly={false}
                            onChange={(t: string) => setName(t)}
                        />
                    </Row>
                    <Row>
                        <Col xl={6} lg={6} md={6} sm={12} xs={12}
                            style={{ marginTop: "1em", display: "inline-flex", gap: "1rem" }}
                        >
                            <ButtonComponent
                                text={intl.formatMessage({ id: "Common.Buttons.Search" })}
                                onClick={onButtonClick}
                                style={{ minWidth: "113px" }}
                            />
                            <ButtonComponent
                                text={intl.formatMessage({ id: "Common.Buttons.RemoveFilters" })}
                                onClick={onRemoveFiltersClick}
                                style={{ minWidth: "113px" }}
                            />
                        </Col>
                    </Row>

                    <Row >
                        <Col xl={12} lg={12} md={12} sm={12} xs={12}>
                            <div className="pageComponent" style={{ width: "100%" }}>
                                <TableComponent
                                    goToPage={goToPage}
                                    loading={loading}
                                    pageCount={pageCount}
                                    columns={columns}
                                    manualPagination={true}
                                    fetchData={fetchData}
                                    pageSizes={pageSizes}
                                    initialPage={page}
                                    initialPageSize={pageSize}
                                    initialSortBy={initialSortBy}
                                    data={data}
                                />
                            </div>
                        </Col>
                    </Row>
                </div>
            </div>
        </Layout>
    )
}

export default PartList


